class ArticleRelatedResources extends HTMLElement {
  connectedCallback() {
    if (this.loaded) return;
    this.loaded = true;
    this.grid = this.querySelector('[data-related-resources-grid]');
    this.limit = Number.parseInt(this.dataset.limit || '8', 10);
    this.articleTitle = this.normalize(this.dataset.primaryQuery || '');
    this.articleContext = this.normalize(document.querySelector('[data-article-content]')?.textContent || '');
    if (!this.grid || !this.dataset.predictiveSearchUrl) return;

    this.fallbackItems = [...this.grid.children];
    this.loadRecommendations();
  }

  async loadRecommendations() {
    const queries = [this.buildQuery(this.dataset.primaryQuery), this.dataset.primaryQuery, this.dataset.secondaryQuery]
      .map((query) => query?.trim())
      .filter((query, index, values) => query && values.indexOf(query) === index);
    const recommendations = [];
    const seenUrls = new Set();

    try {
      for (const query of queries) {
        const results = await this.search(query);
        for (const [resultIndex, resource] of this.interleave(results.products, results.collections).entries()) {
          const resourceUrl = this.cleanUrl(resource.url);
          if (!resourceUrl || seenUrls.has(resourceUrl)) continue;
          seenUrls.add(resourceUrl);
          recommendations.push({ ...resource, searchRank: resultIndex });
        }
        if (recommendations.length >= this.limit + 2) break;
      }
    } catch (_error) {
      return;
    }

    if (recommendations.length === 0) return;

    const fragment = document.createDocumentFragment();
    recommendations
      .sort((first, second) => this.relevanceScore(second) - this.relevanceScore(first))
      .slice(0, this.limit)
      .forEach((resource) => {
        fragment.append(this.createItem(resource));
      });

    for (const fallbackItem of this.fallbackItems) {
      if (fragment.childElementCount >= this.limit) break;
      const fallbackUrl = this.cleanUrl(fallbackItem.querySelector('a')?.href);
      if (fallbackUrl && seenUrls.has(fallbackUrl)) continue;
      if (fallbackUrl) seenUrls.add(fallbackUrl);
      fragment.append(fallbackItem);
    }

    if (fragment.childElementCount === this.limit) this.grid.replaceChildren(fragment);
  }

  async search(query) {
    const url = new URL(this.dataset.predictiveSearchUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Related resources failed with ${response.status}`);
    const payload = await response.json();
    return {
      products: (payload.resources?.results?.products || []).map((product) => ({ ...product, resourceType: 'product' })),
      collections: (payload.resources?.results?.collections || []).map((collection) => ({ ...collection, resourceType: 'collection' })),
    };
  }

  interleave(products, collections) {
    const mixed = [];
    const length = Math.max(products.length, collections.length);
    for (let index = 0; index < length; index += 1) {
      if (products[index]) mixed.push(products[index]);
      if (collections[index]) mixed.push(collections[index]);
    }
    return mixed;
  }

  buildQuery(value) {
    const stopWords = new Set([
      'al', 'con', 'de', 'del', 'el', 'en', 'la', 'las', 'lo', 'los', 'para', 'por', 'que', 'se', 'su', 'sus', 'un', 'una', 'y',
    ]);
    const terms = this.normalize(value || '')
      .split(' ')
      .filter((term, index, values) => term.length >= 3 && !stopWords.has(term) && values.indexOf(term) === index);
    return terms.slice(0, 8).join(' ');
  }

  relevanceScore(resource) {
    const resourceTitle = this.normalize(resource.title || '');
    const titleTerms = resourceTitle.split(' ').filter((term) => term.length >= 4);
    let score = 100 - (resource.searchRank || 0);

    for (const term of titleTerms) {
      if (this.articleTitle.includes(term)) score += 30;
      if (this.articleContext.includes(term)) score += 12;
    }
    return score;
  }

  normalize(value) {
    return value
      .toLocaleLowerCase(document.documentElement.lang || undefined)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  createItem(resource) {
    const item = document.createElement('li');
    const card = document.createElement('article');
    const media = document.createElement('a');
    const content = document.createElement('div');
    const heading = document.createElement('h3');
    const titleLink = document.createElement('a');
    const url = this.cleanUrl(resource.url);
    const imageSource = resource.featured_image?.url || resource.image?.url || resource.image;
    const price = resource.resourceType === 'product' ? Number.parseFloat(resource.price_min || resource.price || 0) : 0;
    const compareAt = resource.resourceType === 'product' ? Number.parseFloat(resource.compare_at_price_min || 0) : 0;

    item.className = `tm-article-resources__item tm-article-resources__item--${resource.resourceType}`;
    card.className = 'tm-article-resource-card';
    media.className = 'tm-article-resource-card__media';
    media.href = url;
    media.setAttribute('aria-label', resource.title || '');

    if (imageSource) {
      const backdrop = document.createElement('span');
      const backdropImage = document.createElement('img');
      const image = document.createElement('img');
      backdrop.className = 'tm-article-resource-card__backdrop';
      backdrop.setAttribute('aria-hidden', 'true');
      backdropImage.className = 'tm-article-resource-card__backdrop-image';
      backdropImage.src = this.resizeImage(imageSource, 280);
      backdropImage.alt = '';
      backdropImage.loading = 'lazy';
      backdropImage.width = 280;
      backdropImage.height = 280;
      backdrop.append(backdropImage);
      image.className = 'tm-article-resource-card__image';
      image.src = this.resizeImage(imageSource, 760);
      image.srcset = [280, 420, 560, 760]
        .map((width) => `${this.resizeImage(imageSource, width)} ${width}w`)
        .join(', ');
      image.sizes = '(min-width: 1200px) 20vw, (min-width: 990px) 25vw, (min-width: 750px) 50vw, 100vw';
      image.alt = resource.featured_image?.alt || resource.title || '';
      image.loading = 'lazy';
      image.width = 760;
      image.height = 760;
      media.append(backdrop, image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'tm-article-resource-card__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      media.append(placeholder);
    }

    if (compareAt > price) media.append(this.createDiscountBadge(price, compareAt));

    content.className = 'tm-article-resource-card__content';
    heading.className = 'tm-article-resource-card__title';
    titleLink.href = url;
    titleLink.textContent = resource.title || '';
    heading.append(titleLink);
    content.append(heading);

    const description = this.toPlainText(resource.body || resource.description || '');
    if (description) {
      const paragraph = document.createElement('p');
      paragraph.className = 'tm-article-resource-card__description';
      paragraph.textContent = this.truncateWords(description, 34);
      content.append(paragraph);
    }

    if (resource.resourceType === 'product') {
      if (price > 0) content.append(this.createPrice(price, compareAt));
    }

    card.append(media, content);
    item.append(card);
    return item;
  }

  createDiscountBadge(price, compareAt) {
    const badge = document.createElement('span');
    const discountPercentage = Math.round(((compareAt - price) * 100) / compareAt);
    badge.className = 'tm-product-price__discount tm-article-resource-card__discount';
    badge.setAttribute('aria-hidden', 'true');
    badge.textContent = `-${discountPercentage}%`;
    return badge;
  }

  createPrice(price, compareAt) {
    const priceContainer = document.createElement('div');
    const currentPrice = document.createElement('span');
    priceContainer.className = 'tm-article-resource-card__price';
    currentPrice.textContent = this.formatMoney(price);
    if (compareAt > price) {
      currentPrice.className = 'tm-article-resource-card__price--sale';
      const oldPrice = document.createElement('s');
      oldPrice.textContent = this.formatMoney(compareAt);
      priceContainer.append(currentPrice, oldPrice);
    } else {
      priceContainer.append(currentPrice);
    }
    return priceContainer;
  }

  cleanUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      return `${url.pathname}${url.hash}`;
    } catch (_error) {
      return value;
    }
  }

  resizeImage(source, width) {
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_error) {
      return source;
    }
  }

  toPlainText(html) {
    const template = document.createElement('template');
    template.innerHTML = html;
    template.content.querySelectorAll('style, script, noscript').forEach((element) => element.remove());
    return (template.content.textContent || '').replace(/\s+/g, ' ').trim();
  }

  truncateWords(text, limit) {
    const words = text.split(/\s+/);
    return words.length > limit ? `${words.slice(0, limit).join(' ')}…` : text;
  }

  formatMoney(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
      }).format(value);
    } catch (_error) {
      return `${value.toFixed(2)} ${this.dataset.currency || ''}`.trim();
    }
  }
}

if (!customElements.get('article-related-resources')) {
  customElements.define('article-related-resources', ArticleRelatedResources);
}
